/**
 * Returns a new set containing elements that are in setA but not in setB.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/difference
 *
 * Implementation loosely based on [CoreJS polyfill implementation](https://github.com/zloirock/core-js/blob/master/packages/core-js/internals/set-difference.js).
 */
function difference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const result = new Set<T>();
  setA.forEach((item) => {
    if (!setB.has(item)) {
      result.add(item);
    }
  });
  return result;
}

/**
 * Returns a new set containing elements that are in either or both setA and setB.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/union
 *
 *
 * Implementation loosely based on [CoreJS polyfill implementation](https://github.com/zloirock/core-js/blob/master/packages/core-js/internals/set-union.js).
 */
function union<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const result = new Set<T>(setA);
  setB.forEach((item) => {
    result.add(item);
  });
  return result;
}

/**
 * Some utility functions for working with Sets in JavaScript, since our target browsers don't yet have it widely supported from the [Set Methods proposal](https://github.com/tc39/proposal-set-methods).
 * These methods aren't yet noted in the ECMAScript standard, and from [this compatibility table](https://compat-table.github.io/compat-table/es2016plus/), it looks like they are part of the ES2025 and are currently newly available.
 */
export const setMethods = {
  difference,
  union,
};
